import { useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Loader2, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { HumanCheck } from "./HumanCheck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  sk: {
    role: "Riaditeľ pre marketing, spoluprácu a styk s verejnosťou",
    btn: "Poslať správu",
    title: "Marketing a spolupráca",
    sub: "Napíšte nám ohľadom marketingu, spolupráce alebo partnerstva.",
    name: "Meno", email: "Váš e-mail", company: "Firma (nepovinné)", message: "Správa",
    consent: "Súhlasím so spracovaním údajov na účel vybavenia správy.",
    send: "Odoslať správu", sending: "Odosielam…",
    ok: "Ďakujeme, Vaša správa bola odoslaná. Čoskoro sa Vám ozveme.",
    err: "Správu sa nepodarilo odoslať. Skúste to znova.",
  },
  en: {
    role: "Director of Marketing, Cooperation & Public Relations",
    btn: "Send a message",
    title: "Marketing & cooperation",
    sub: "Write to us about marketing, cooperation or partnership.",
    name: "Name", email: "Your e-mail", company: "Company (optional)", message: "Message",
    consent: "I agree to the processing of my data to handle this message.",
    send: "Send message", sending: "Sending…",
    ok: "Thank you, your message has been sent. We will get back to you soon.",
    err: "The message could not be sent. Please try again.",
  },
  de: {
    role: "Direktor für Marketing, Zusammenarbeit & Öffentlichkeitsarbeit",
    btn: "Nachricht senden",
    title: "Marketing & Zusammenarbeit",
    sub: "Schreiben Sie uns zu Marketing, Zusammenarbeit oder Partnerschaft.",
    name: "Name", email: "Ihre E-Mail", company: "Firma (optional)", message: "Nachricht",
    consent: "Ich stimme der Verarbeitung meiner Daten zur Bearbeitung zu.",
    send: "Nachricht senden", sending: "Senden…",
    ok: "Danke, Ihre Nachricht wurde gesendet. Wir melden uns bald.",
    err: "Die Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.",
  },
  pl: {
    role: "Dyrektor ds. marketingu, współpracy i public relations",
    btn: "Wyślij wiadomość",
    title: "Marketing i współpraca",
    sub: "Napisz do nas w sprawie marketingu, współpracy lub partnerstwa.",
    name: "Imię", email: "Twój e-mail", company: "Firma (opcjonalnie)", message: "Wiadomość",
    consent: "Wyrażam zgodę na przetwarzanie danych w celu obsługi wiadomości.",
    send: "Wyślij wiadomość", sending: "Wysyłanie…",
    ok: "Dziękujemy, wiadomość została wysłana. Wkrótce się odezwiemy.",
    err: "Nie udało się wysłać wiadomości. Spróbuj ponownie.",
  },
  fr: {
    role: "Directrice du marketing, de la coopération et des relations publiques",
    btn: "Envoyer un message",
    title: "Marketing & coopération",
    sub: "Écrivez-nous au sujet du marketing, de la coopération ou d'un partenariat.",
    name: "Nom", email: "Votre e-mail", company: "Société (facultatif)", message: "Message",
    consent: "J'accepte le traitement de mes données aux fins du traitement de ce message.",
    send: "Envoyer le message", sending: "Envoi en cours…",
    ok: "Merci, votre message a été envoyé. Nous reviendrons vers vous bientôt.",
    err: "Le message n'a pas pu être envoyé. Veuillez réessayer.",
  },
};

export const MarketingContact = () => {
  const { lang } = useLang();
  const tx = L[lang] || L.sk;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", consent: false, website: "" });
  const [state, setState] = useState("idle"); // idle | sending | ok | err
  const [hToken, setHToken] = useState("");
  const [hAnswer, setHAnswer] = useState("");
  const [hReload, setHReload] = useState(0);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || !form.consent || !hAnswer.trim()) return;
    setState("sending");
    try {
      await axios.post(`${API}/contact/marketing`, {
        name: form.name.trim(), email: form.email.trim(), company: form.company.trim() || null,
        message: form.message.trim(), consent: form.consent, language: lang, website: form.website,
        human_token: hToken, human_answer: hAnswer,
      });
      setState("ok");
    } catch {
      setState("err");
      setHAnswer("");
      setHReload((x) => x + 1);
    }
  };

  const close = () => { setOpen(false); setTimeout(() => { setState("idle"); setHAnswer(""); setHReload((x) => x + 1); setForm({ name: "", email: "", company: "", message: "", consent: false, website: "" }); }, 200); };

  return (
    <div className="mt-6 pt-5 border-t border-white/10" data-testid="footer-marketing-contact">
      <p className="text-white/55 text-sm leading-snug">{tx.role}</p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="marketing-contact-open"
        className="inline-flex items-center gap-2 mt-3 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors"
      >
        <MessageSquare size={15} /> {tx.btn}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-4" data-testid="marketing-contact-modal" onClick={close}>
          <div className="w-full max-w-md border border-[#D4AF37]/25 bg-[#0a0a0a] p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-2xl text-white">{tx.title}</h3>
                <p className="text-white/45 text-sm mt-1">{tx.sub}</p>
              </div>
              <button onClick={close} data-testid="marketing-contact-close" className="text-white/50 hover:text-white shrink-0"><X size={20} /></button>
            </div>

            {state === "ok" ? (
              <div className="mt-8 text-center py-6" data-testid="marketing-contact-success">
                <div className="mx-auto w-12 h-12 flex items-center justify-center bg-[#D4AF37] text-black rounded-full mb-4"><Check size={24} /></div>
                <p className="text-white/80">{tx.ok}</p>
                <button onClick={close} className="mt-6 text-[#D4AF37] hover:text-[#F2E3C6] text-sm">OK</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <div>
                  <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={tx.name} data-testid="marketing-contact-name" required
                    className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={tx.email} data-testid="marketing-contact-email" required
                    className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder={tx.company} data-testid="marketing-contact-company"
                    className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
                </div>
                <div>
                  <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={tx.message} data-testid="marketing-contact-message" required rows={5}
                    className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-none" />
                </div>
                <label className="flex items-start gap-2 text-white/55 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="marketing-contact-consent" className="mt-0.5 accent-[#D4AF37]" />
                  <span>{tx.consent}</span>
                </label>
                {state === "err" && <p className="text-red-400 text-sm" data-testid="marketing-contact-error">{tx.err}</p>}
                <HumanCheck lang={lang} answer={hAnswer} onAnswer={setHAnswer} onToken={setHToken} reloadKey={hReload} />
                <button type="submit" disabled={state === "sending" || !form.name.trim() || !form.email.trim() || !form.message.trim() || !form.consent || !hAnswer.trim()} data-testid="marketing-contact-send"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">
                  {state === "sending" ? <><Loader2 size={16} className="animate-spin" /> {tx.sending}</> : <><Send size={16} /> {tx.send}</>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
